// src/hooks/useCommunicationLog.js
// Lightweight communication log for tracking email drafts opened from the app.
// Uses settings_audit_log table with action prefix 'EMAIL_DRAFT_OPENED'.
// Non-fatal: log failures never block the save/email flow.
//
// IMPORTANT: The app only generates drafts and opens the mail client.
// Actual sending occurs in the user's mail app (Outlook/Apple Mail).
// Status is always 'draft_opened', never 'sent'.
import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const ACTION = 'EMAIL_DRAFT_OPENED';

/**
 * Hook for logging and reading communication events per project.
 * Writes to settings_audit_log with structured payload.
 */
export default function useCommunicationLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Log an email draft event (non-fatal).
   * Status is always 'draft_opened' — the app opens the mail client but cannot
   * confirm whether the user actually pressed Send.
   *
   * @param {object} params
   * @param {number} params.projectId
   * @param {number} [params.clientId]
   * @param {number} [params.invoiceId]
   * @param {string[]} params.to
   * @param {string[]} [params.cc]
   * @param {string[]} [params.bcc]
   * @param {string} params.subject
   * @param {string} params.eventType - 'stage_initiated' | 'stage_approved' | 'invoice_sent' | 'reminder_*'
   * @param {string} [params.templateUsed] - Template key used for the draft
   * @param {string} [params.stageName]
   * @param {string} [params.dateValue] - The timeline date (YYYY-MM-DD)
   */
  const logEmail = useCallback(async (params) => {
    try {
      const userId = (await supabase.auth.getUser())?.data?.user?.id || null;
      await supabase.from('settings_audit_log').insert([{
        actor_user_id: userId,
        action: ACTION,
        payload: {
          type: params.eventType || '',
          status: 'draft_opened',
          projectId: params.projectId,
          clientId: params.clientId || null,
          invoiceId: params.invoiceId || null,
          to: params.to || [],
          cc: params.cc || [],
          bcc: params.bcc || [],
          subject: params.subject || '',
          eventType: params.eventType || '',
          templateUsed: params.templateUsed || params.eventType || '',
          stageName: params.stageName || '',
          dateValue: params.dateValue || '',
          createdAt: new Date().toISOString(),
        },
      }]);
    } catch (err) {
      console.warn('[useCommunicationLog] Log error (non-fatal):', err);
    }
  }, []);

  /**
   * Fetch communication logs for a given project.
   * Returns most recent first.
   */
  const fetchLogsForProject = useCallback(async (projectId) => {
    if (!projectId) return [];
    setLoading(true);
    try {
      // Use server-side JSONB filtering via Supabase .or() on payload->projectId.
      // projectId may be stored as number or string in JSONB, so query both forms.
      const numId = Number(projectId);
      const strId = String(projectId);
      const { data, error } = await supabase
        .from('settings_audit_log')
        .select('id, action, payload, created_at, actor_user_id')
        .eq('action', ACTION)
        .or(`payload->>projectId.eq.${strId},payload->projectId.eq.${numId}`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setLogs(data || []);
      return data || [];
    } catch (err) {
      console.warn('[useCommunicationLog] Fetch error:', err);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  return { logs, loading, logEmail, fetchLogsForProject };
}
